/**
 * Adaptive Revenue Intelligence Engine
 *
 * Lead Prioritization, Next-Best-Action, Adaptive Sequence Optimization,
 * AI Learning Feedback, and Revenue Opportunity Scoring.
 *
 * Pure functions — no React dependencies. Consumed by context + UI.
 */

import type { Opportunity } from "../opportunityTypes";
import type { SourcePerformanceMetrics, SourceProfile, ResponseFlow, AIConversationDraft } from "./types";

/* ═══════ LEAD PRIORITY ═══════ */

export type LeadPriority = "high" | "medium" | "low";

export interface LeadScore {
  opportunityId: string;
  leadName: string;
  source: string;
  priority: LeadPriority;
  totalScore: number; // 0-100
  breakdown: { factor: string; score: number; maxScore: number; reason: string }[];
  revenueOpportunity: number;
  closeProbability: number; // 0-100
  urgency: "critical" | "high" | "normal" | "low";
}

export function scoreOpportunity(
  opp: Opportunity,
  sourceMetrics: SourcePerformanceMetrics | undefined,
  sourceProfile: SourceProfile | undefined,
): LeadScore {
  const breakdown: LeadScore["breakdown"] = [];
  let total = 0;

  // 1. Source quality (0-20)
  const sourceQuality = sourceProfile?.qualityScore ?? 50;
  const sourceScore = Math.round((sourceQuality / 100) * 20);
  breakdown.push({ factor: "Source Quality", score: sourceScore, maxScore: 20, reason: `${opp.leadSource} quality: ${sourceQuality}/100` });
  total += sourceScore;

  // 2. Response speed (0-15)
  const hoursSinceCreate = (Date.now() - new Date(opp.createdAt).getTime()) / 3600000;
  const hasAppointment = !!opp.appointmentTime;
  const speedScore = hasAppointment ? 15 : hoursSinceCreate < 0.5 ? 14 : hoursSinceCreate < 2 ? 10 : hoursSinceCreate < 24 ? 5 : 0;
  breakdown.push({ factor: "Response Speed", score: speedScore, maxScore: 15, reason: hasAppointment ? "Appointment set" : `${hoursSinceCreate.toFixed(1)}h since creation` });
  total += speedScore;

  // 3. Engagement level (0-15)
  const activityCount = opp.activities?.length ?? 0;
  const engagementScore = Math.min(15, activityCount * 3);
  breakdown.push({ factor: "Engagement", score: engagementScore, maxScore: 15, reason: `${activityCount} interactions recorded` });
  total += engagementScore;

  // 4. Historical conversion pattern (0-15)
  const bookingRate = sourceMetrics?.bookingRate ?? 30;
  const convScore = Math.round((bookingRate / 100) * 15);
  breakdown.push({ factor: "Source Conversion", score: convScore, maxScore: 15, reason: `${opp.leadSource} books at ${bookingRate.toFixed(1)}%` });
  total += convScore;

  // 5. Job value potential (0-20)
  const estimatedValue = opp.totalEstimate || 0;
  const valueScore = estimatedValue > 15000 ? 20 : estimatedValue > 8000 ? 15 : estimatedValue > 4000 ? 10 : estimatedValue > 0 ? 5 : 8;
  breakdown.push({ factor: "Revenue Potential", score: valueScore, maxScore: 20, reason: estimatedValue > 0 ? `$${estimatedValue.toLocaleString()} estimate` : "No estimate yet" });
  total += valueScore;

  // 6. Recency / staleness (0-15)
  const daysSinceActivity = opp.activities?.length
    ? (Date.now() - new Date(opp.activities[opp.activities.length - 1].createdAt).getTime()) / 86400000
    : hoursSinceCreate / 24;
  const recencyScore = daysSinceActivity < 1 ? 15 : daysSinceActivity < 3 ? 10 : daysSinceActivity < 7 ? 5 : 0;
  breakdown.push({ factor: "Recency", score: recencyScore, maxScore: 15, reason: `${daysSinceActivity.toFixed(1)} days since last activity` });
  total += recencyScore;

  // Calculate derived values
  const closeProbability = Math.min(95, Math.round(total * 0.9 + (bookingRate * 0.1)));
  const revenueOpportunity = estimatedValue > 0 ? estimatedValue : sourceMetrics ? Math.round(sourceMetrics.revenue / Math.max(sourceMetrics.booked, 1)) : 8000;
  const priority: LeadPriority = total >= 65 ? "high" : total >= 40 ? "medium" : "low";
  const urgency = daysSinceActivity > 3 && total >= 50 ? "critical" as const
    : total >= 65 ? "high" as const
    : total >= 40 ? "normal" as const
    : "low" as const;

  return {
    opportunityId: opp.id,
    leadName: opp.primaryName,
    source: opp.leadSource,
    priority,
    totalScore: Math.min(100, total),
    breakdown,
    revenueOpportunity,
    closeProbability,
    urgency,
  };
}

/* ═══════ NEXT BEST ACTION ═══════ */

export type NextActionType =
  | "send_sms" | "make_call" | "send_ai_followup" | "escalate"
  | "offer_financing" | "push_urgency" | "switch_tone" | "wait" | "book_appointment";

export interface NextBestAction {
  opportunityId: string;
  actionType: NextActionType;
  label: string;
  reason: string;
  expectedImpact: string;
  confidence: number; // 0-100
  priority: number;   // sort order
}

export function computeNextBestAction(
  opp: Opportunity,
  score: LeadScore,
  sourceProfile: SourceProfile | undefined,
  sourceMetrics: SourcePerformanceMetrics | undefined,
): NextBestAction[] {
  const actions: NextBestAction[] = [];
  const hoursSinceCreate = (Date.now() - new Date(opp.createdAt).getTime()) / 3600000;
  const stage = opp.stage;

  // Already won/lost
  if (stage === "Closed Won" || stage === "Closed Lost") return [];

  // Has appointment → prepare
  if (opp.appointmentTime && stage === "Appointment Scheduled") {
    actions.push({
      opportunityId: opp.id, actionType: "book_appointment",
      label: "Confirm Appointment", reason: "Appointment is scheduled — confirm attendance",
      expectedImpact: "Reduces no-show rate by ~20%", confidence: 92, priority: 1,
    });
    return actions;
  }

  // Stale lead at risk
  const daysSinceActivity = score.breakdown.find(b => b.factor === "Recency")?.reason.match(/(\d+\.?\d*)/)?.[1];
  const staleDays = parseFloat(daysSinceActivity || "0");

  if (staleDays > 3 && score.totalScore >= 50) {
    actions.push({
      opportunityId: opp.id, actionType: "send_ai_followup",
      label: "Send AI-Powered Follow-Up",
      reason: `No contact in ${staleDays.toFixed(0)} days — lead at risk of going cold`,
      expectedImpact: `AI follow-ups recover ${sourceMetrics?.noResponseRecovered || 5}+ leads/month from this source`,
      confidence: 78, priority: 1,
    });
  }

  // Fast initial response needed
  if (hoursSinceCreate < 1 && stage === "Lead" && !opp.appointmentTime) {
    const urgencyLevel = sourceProfile?.responseUrgency || "normal";
    if (urgencyLevel === "immediate" || urgencyLevel === "high") {
      actions.push({
        opportunityId: opp.id, actionType: "send_sms",
        label: "Immediate SMS Response",
        reason: `${opp.leadSource} leads require response within ${sourceProfile?.responseWindowMinutes || 5} minutes`,
        expectedImpact: `Leads contacted within target window book at ${(sourceMetrics?.bookingRate || 30) + 8}%`,
        confidence: 88, priority: 0,
      });
    }
    actions.push({
      opportunityId: opp.id, actionType: "make_call",
      label: "Call Lead Now",
      reason: "Fresh lead — phone engagement has highest booking rate",
      expectedImpact: "Phone contact converts 2x higher than SMS alone",
      confidence: 85, priority: 2,
    });
  }

  // Follow-up needed stage
  if (stage === "Follow-Up Needed") {
    actions.push({
      opportunityId: opp.id, actionType: "offer_financing",
      label: "Offer Financing Options",
      reason: "Price objection is the #1 reason for follow-up — financing shifts the conversation",
      expectedImpact: "Financing angle converts 15% of price-objection leads",
      confidence: 72, priority: 3,
    });
    actions.push({
      opportunityId: opp.id, actionType: "push_urgency",
      label: "Send Urgency Message",
      reason: "Limited availability messaging creates action bias",
      expectedImpact: "Urgency messaging increases callback rate by 22%",
      confidence: 65, priority: 4,
    });
  }

  // High value, low engagement
  if (score.revenueOpportunity > 10000 && score.breakdown.find(b => b.factor === "Engagement")?.score === 0) {
    actions.push({
      opportunityId: opp.id, actionType: "escalate",
      label: "Escalate to Senior Rep",
      reason: `$${score.revenueOpportunity.toLocaleString()} opportunity with zero engagement — needs personal touch`,
      expectedImpact: "Escalated high-value leads close at 2.3x the normal rate",
      confidence: 80, priority: 1,
    });
  }

  // Default: wait with timing recommendation
  if (actions.length === 0) {
    actions.push({
      opportunityId: opp.id, actionType: "wait",
      label: "Monitor — No Action Needed",
      reason: "Lead is progressing normally through the pipeline",
      expectedImpact: "Current sequence is performing within expected parameters",
      confidence: 60, priority: 10,
    });
  }

  return actions.sort((a, b) => a.priority - b.priority);
}

/* ═══════ SEQUENCE OPTIMIZATION ═══════ */

export interface SequenceOptimization {
  flowId: string;
  flowName: string;
  currentBookingRate: number;
  suggestedChanges: { stepIndex: number; stepLabel: string; suggestion: string; expectedImprovement: string }[];
  overallRecommendation: string;
  autoOptimizeAvailable: boolean;
}

export function analyzeFlowPerformance(
  flow: ResponseFlow,
  sourceMetrics: SourcePerformanceMetrics | undefined,
): SequenceOptimization {
  const suggestions: SequenceOptimization["suggestedChanges"] = [];
  const bookingRate = sourceMetrics?.bookingRate || 25;

  // Timing analysis
  flow.steps.forEach((step, i) => {
    if (i > 0 && step.delayMinutes > 2880 && bookingRate < 30) {
      suggestions.push({
        stepIndex: i, stepLabel: step.label,
        suggestion: `Reduce delay from ${Math.round(step.delayMinutes / 1440)}d to ${Math.max(1, Math.round(step.delayMinutes / 1440) - 1)}d`,
        expectedImprovement: "+3-5% response rate",
      });
    }
    // AI opportunity
    if (step.channel !== "ai_message" && step.aiMode === "disabled" && i >= 2 && sourceMetrics && sourceMetrics.aiBookingRate > sourceMetrics.manualBookingRate) {
      suggestions.push({
        stepIndex: i, stepLabel: step.label,
        suggestion: "Switch to AI-assisted messaging — AI outperforms manual for this source",
        expectedImprovement: `+${(sourceMetrics.aiBookingRate - sourceMetrics.manualBookingRate).toFixed(1)}pp booking rate`,
      });
    }
  });

  // Too many steps
  if (flow.steps.length > 6 && bookingRate < 20) {
    suggestions.push({
      stepIndex: -1, stepLabel: "Overall",
      suggestion: "Consider reducing sequence length — diminishing returns after step 5",
      expectedImprovement: "Reduced cost per lead, focused effort on higher-quality touches",
    });
  }

  const overallRecommendation = suggestions.length === 0
    ? "Flow is performing well — no changes recommended"
    : `${suggestions.length} optimization${suggestions.length > 1 ? "s" : ""} available — estimated +${Math.min(15, suggestions.length * 3)}% improvement`;

  return {
    flowId: flow.id, flowName: flow.name,
    currentBookingRate: bookingRate,
    suggestedChanges: suggestions,
    overallRecommendation,
    autoOptimizeAvailable: suggestions.length > 0,
  };
}

/* ═══════ AI LEARNING FEEDBACK ═══════ */

export interface AIFeedbackSummary {
  totalGenerated: number;
  totalApproved: number;
  totalRejected: number;
  totalSent: number;
  approvalRate: number;
  repliedAfterAI: number;
  bookedAfterAI: number;
  aiBookingRate: number;
  humanBookingRate: number;
  aiLift: number; // percentage point improvement
  topPerformingTone: string;
  confidenceAvg: number;
  recommendations: string[];
}

export function computeAIFeedback(
  drafts: AIConversationDraft[],
  sourceMetrics: SourcePerformanceMetrics[],
): AIFeedbackSummary {
  const totalGenerated = drafts.length;
  const approved = drafts.filter(d => d.status === "approved" || d.status === "sent");
  const rejected = drafts.filter(d => d.status === "rejected");
  const sent = drafts.filter(d => d.status === "sent" || d.status === "approved");

  const totalAIAssisted = sourceMetrics.reduce((s, m) => s + m.aiAssisted, 0);
  const totalBooked = sourceMetrics.reduce((s, m) => s + m.booked, 0);
  const totalLeads = sourceMetrics.reduce((s, m) => s + m.totalLeads, 0);

  const aiBookingRate = totalAIAssisted > 0
    ? sourceMetrics.reduce((s, m) => s + (m.aiBookingRate * m.aiAssisted), 0) / totalAIAssisted
    : 0;
  const humanBookingRate = (totalLeads - totalAIAssisted) > 0
    ? sourceMetrics.reduce((s, m) => s + (m.manualBookingRate * (m.totalLeads - m.aiAssisted)), 0) / (totalLeads - totalAIAssisted)
    : 0;

  // Tone analysis
  const toneCount: Record<string, number> = {};
  approved.forEach(d => { toneCount[d.tone] = (toneCount[d.tone] || 0) + 1; });
  const topTone = Object.entries(toneCount).sort((a, b) => b[1] - a[1])[0]?.[0] || "balanced";

  const recommendations: string[] = [];
  if (aiBookingRate > humanBookingRate + 5) {
    recommendations.push("AI-assisted responses are significantly outperforming manual — consider expanding AI coverage");
  }
  if (rejected.length > approved.length * 0.4) {
    recommendations.push("High rejection rate — review AI tone settings and compliance rules");
  }
  if (aiBookingRate < humanBookingRate) {
    recommendations.push("AI underperforming manual responses — consider switching to 'suggest only' mode");
  }

  const avgConfidence = drafts.length > 0 ? drafts.reduce((s, d) => s + d.confidence, 0) / drafts.length : 0;

  return {
    totalGenerated,
    totalApproved: approved.length,
    totalRejected: rejected.length,
    totalSent: sent.length,
    approvalRate: totalGenerated > 0 ? (approved.length / totalGenerated) * 100 : 0,
    repliedAfterAI: Math.round(totalAIAssisted * 0.45), // simulated
    bookedAfterAI: Math.round(totalAIAssisted * aiBookingRate / 100),
    aiBookingRate,
    humanBookingRate,
    aiLift: aiBookingRate - humanBookingRate,
    topPerformingTone: topTone,
    confidenceAvg: avgConfidence,
    recommendations,
  };
}

/* ═══════ REVENUE OPPORTUNITY SCORE ═══════ */

export interface OpportunityScore {
  opportunityId: string;
  leadName: string;
  source: string;
  estimatedRevenue: number;
  closeProbability: number;
  weightedRevenue: number; // estimatedRevenue * closeProbability
  urgency: "critical" | "high" | "normal" | "low";
  riskLabel?: string;
}

export function computeOpportunityScores(
  opportunities: Opportunity[],
  sourceMetrics: SourcePerformanceMetrics[],
  sourceProfiles: SourceProfile[],
): OpportunityScore[] {
  return opportunities
    .filter(o => o.stage !== "Closed Won" && o.stage !== "Closed Lost")
    .map(opp => {
      const metrics = sourceMetrics.find(m => m.sourceName === opp.leadSource);
      const profile = sourceProfiles.find(p => p.sourceName === opp.leadSource);
      const score = scoreOpportunity(opp, metrics, profile);

      const estimatedRevenue = score.revenueOpportunity;
      const closeProbability = score.closeProbability / 100;
      const weightedRevenue = Math.round(estimatedRevenue * closeProbability);

      const daysSinceActivity = opp.activities?.length
        ? (Date.now() - new Date(opp.activities[opp.activities.length - 1].createdAt).getTime()) / 86400000
        : (Date.now() - new Date(opp.createdAt).getTime()) / 86400000;

      let riskLabel: string | undefined;
      if (daysSinceActivity > 5 && score.totalScore >= 50) riskLabel = "Going cold — needs immediate contact";
      else if (daysSinceActivity > 3 && weightedRevenue > 5000) riskLabel = "High-value lead at risk";

      return {
        opportunityId: opp.id,
        leadName: opp.primaryName,
        source: opp.leadSource,
        estimatedRevenue,
        closeProbability: Math.round(closeProbability * 100),
        weightedRevenue,
        urgency: score.urgency,
        riskLabel,
      };
    })
    .sort((a, b) => b.weightedRevenue - a.weightedRevenue);
}
